import React, { useContext, useEffect, useState } from "react";
import { EmpdContext } from "../../Context";
import { Document, Page, PDFDownloadLink } from "@react-pdf/renderer";
import { createUseStyles } from "react-jss";
import { pick } from 'lodash'
import classNames from "classnames";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import { TextField } from '@mui/material';
import Button from "../Button";
import { globalStyles } from "../../Styles";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertFromHTML, convertToRaw, convertFromRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { cloneDeep } from 'lodash'
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser"
import MsalLoadingComponent from "..//Msal/MsalLoadlingComponent"
import NotAuthorized from "../NotAuthorized";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

interface Props {
  className?: string;
  type: string | undefined;
  content: string | undefined;
  header: string | undefined;
  setHeader: (value: string | undefined) => void;
  showMessage: boolean;
  setShowMessage: (value: boolean) => void;
  setSelectedValue: (value: any|null) => void;
}

interface ResultProps {
	data: any;
	id: string;
}

const useStyles = createUseStyles({
  root: {},
  dataSummaryWrapper: {},
  dataSummary: {
    marginBottom: 50,
  },
  dataSummaryHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "1px solid #e1e5ed",
    // "& h4": {
    //   margin: 0,
    // },
  },
  dataSummaryTypeCol: {
    flexBasis: "75%",
  },
  dataSummaryStatusCol: {
    flexBasis: "25%",
    textAlign: "right",
  },
  tableHead: {
    background: "#152E54",
    color: "#fff",
    padding: 10,
    margin: 0,
  },
  statBall: {
    width: 15,
    height: 15,
    borderRadius: "50%",
    marginRight: 10,
  },
  statCol: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "end",
    alignItems: "center",
  },
  control: {
    marginTop: 75,
    marginBottom: 30,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    width: "100%",
    paddingBlock: "0.75rem",
    touchAction: "none",
    userSelect: "none",
  },
  track: {
    width: "100%",
    height: "1rem",
    background:
      "linear-gradient(90deg, #3660a2, lightblue, yellow, orange, #ff6464)",
    borderRadius: "0.25rem",
    userSelect: "none",
  },
  indicator: {
    borderRadius: "0.25rem",
    userSelect: "none",
  },
  thumb: {
    marginTop: 10,
    width: "0",
    height: "0",
    borderLeft: "15px solid transparent",
    borderRight: "15px solid transparent",
    borderBottom: "35px solid #152E54",
    "& h5": {
      paddingTop: 20,
      width: 100,
      transform: "translateX(-45%)",
    },
  },
  slider: {
    width: "100%",
  },
  others: {
    width: 175,
    height: 60,
    backgroundColor: "#152e541c",
    "& h5": {
      marginTop: -35,
      width: 175,
      textAlign: "center",
    },
  },
  heroButton: {
    alignSelf: "flex-start",
    marginTop: "20px",
    display: "inline-block",
  },
  activeLink: { 
    "&:hover": {
      cursor: "pointer",
    },
  }
});

const useMyContext = () => {
  const context = useContext(EmpdContext);
  if (!context) {
    throw new Error("No provider found for Context");
  }
  return context;
};

const EditModal = ({ className, type, content, header, setHeader, showMessage, setShowMessage, setSelectedValue }: Props) => {
  const context = useMyContext();
  const {
  	editorState,
	setEditorState,
	openEditDialog,
	setEditDialogOpen,
	writeMessageContent,
	fetchMessageContent,
  } = context;


  const classes = useStyles();

  const handleClick = () => {
  if( type !== undefined) {
  	writeMessageContent(type, draftToHtml(convertToRaw(editorState.getCurrentContent())), header, showMessage)
	fetchMessageContent()
	setSelectedValue(null)
    setEditDialogOpen(false)
	}
  };

  useEffect(() => {
    if( content !== undefined ) { 
	    if( editorState === undefined ) {
			const blocksFromHTML = convertFromHTML(content)
			const contentState = ContentState.createFromBlockArray(
				blocksFromHTML.contentBlocks,
				blocksFromHTML.entityMap
			)
    	    setEditorState(EditorState.createWithContent(contentState))
    	}
	}
  },[])

  useEffect(() => {
    if( content !== undefined ) { 
		const blocksFromHTML = convertFromHTML(content)
		const contentState = ContentState.createFromBlockArray(
			blocksFromHTML.contentBlocks,
			blocksFromHTML.entityMap
		)
        setEditorState(EditorState.createWithContent(contentState))
	}
  },[content])

  const handleClose = () => {
  	setSelectedValue(null)
	setEditDialogOpen(false)
  }

  const onEditorStateChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState)
  }

  const capitalizeWords = (input:string | undefined) => {
    if( input !== undefined ) {
      const words = input.toLowerCase().split('_')
	  const capitalizedWords = words.map(word => {
	    if (word.length > 0) {
      	  return word.charAt(0).toUpperCase() + word.slice(1)
	    }
    	return word 
	  })
  	  return capitalizedWords.join(' ')
    }
  }

  const options = [{ label: 'Yes', value: true },
                   { label: 'No', value: false }]

  const render = (editorState: EditorState, setEditorState:(state:EditorState) => void, setHeader:(value: string) => void, setShowMessage:(value: boolean) => void) => {
    return (
	<>
	  <div className='row' key={type}>
        <div className='smart-col-content-label long-label' key={`${type}-label`}>
          <strong>{`${capitalizeWords(type)} Header:`}</strong>
        </div>
        <TextField key={`${type}-component`}
          data-testid={`test-${type}`}
          label={`Header: `}
          sx={{
            width: 500,
            "& .MuiOutlinedInput-root": {
              color: "#000",
              // Class for the border around the input field
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#002F6C",
                borderWidth: "2px",
              },
            },
            // Class for the label ofthe input field
            "& .MuiInputLabel-outlined": {
              color: "#777777",
            },
            "& .MuiOutlinedInput-input": {
              color: "#002F6C",
            },
          }}
          type='text'
		  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
		  	setHeader(e.target.value)
          }}
          InputProps={{
            style:{
              color: '#777777',
            }
          }}
          value={header}
        />
      </div>
	  <div className={`row`} >
	  	<div className="smart-col-content-label long-label" key={`${type}`} data-testid={`test-${type}`}>
          <strong>{`Show Message: `}</strong>
        </div>
        <Autocomplete
          sx={{
            width: 500,
            "& .MuiOutlinedInput-root": {
              color: "#002F6C",
              // Class for the border around the input field
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#002F6C",
                borderWidth: "2px",
              },
            },
            // Class for the label ofthe input field
            "& .MuiInputLabel-outlined": {
              color: "#002F6C",
            },
          }}
          onChange={(event: React.SyntheticEvent, value: any) => {
		  	setShowMessage(event ? value.value : false)
          }}
          renderInput={(params) => <TextField {...params} label={`Show Message`} />}
          options={options}
		  value={options.find((option:any) => option.value === showMessage)}
        />
	  </div>
	  <div className={`row`} >
	  	{'Message Content:'}
        <div className="demo-root">
          <Editor
            editorState={editorState}
            wrapperClassName="demo-wrapper"
            editorClassName="demo-editor"
            onEditorStateChange={onEditorStateChange}
          />
        </div>
	  </div>
	</>
    )
  }

  return (
	<MsalAuthenticationTemplate
    	interactionType={InteractionType.Redirect}
        errorComponent={NotAuthorized}
        loadingComponent={MsalLoadingComponent}
    >
		
      <Dialog
        open={openEditDialog}
        onClose={handleClose}
        maxWidth={"md"}
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <h3>{`Edit ${capitalizeWords(type)}`}</h3>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
			{render(editorState, setEditorState, setHeader, setShowMessage)}			
        </DialogContent>
        <DialogActions>
          <Button className={`disabled`} onClick={() => handleClick()} label={`Save ${capitalizeWords(type)}`} />
        </DialogActions>
      </Dialog>
    </MsalAuthenticationTemplate>
  );
};

export default EditModal;
